import React, { useState } from 'react';
import { Button, Input, Card, Tag, Steps, message, Typography, Divider, Space, Alert } from 'antd';
import { MailOutlined, UserOutlined, PhoneOutlined, CheckCircleFilled, SendOutlined, RocketOutlined } from '@ant-design/icons';

const { Title, Text, Paragraph } = Typography;

const INTEGRATIONS_SERVER = "http://localhost:5001";

const QUESTIONS = [
  { key: "name", type: "name", icon: <UserOutlined />, question: "👋 Hey! Before we continue, what's your name?", placeholder: "e.g. Hariesh Kumar" },
  { key: "email", type: "email", icon: <MailOutlined />, question: "📧 Great! Please share your email for the free guide.", placeholder: "e.g. hariesh@gmail.com" },
  { key: "phone", type: "phone", icon: <PhoneOutlined />, question: "📱 Last one — your WhatsApp number for updates?", placeholder: "e.g. +919876543210" },
];

export default function TestMailchimpFlow() {
  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [currentAnswer, setCurrentAnswer] = useState("");
  const [flowComplete, setFlowComplete] = useState(false);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);

  // Mailchimp config
  const [apiKey, setApiKey] = useState("");
  const [server, setServer] = useState("");

  const handleAnswer = () => {
    if (!currentAnswer.trim()) return;
    const q = QUESTIONS[currentStep];
    const newAnswers = { ...answers, [q.key]: currentAnswer.trim() };
    setAnswers(newAnswers);
    setCurrentAnswer("");

    if (currentStep < QUESTIONS.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      setFlowComplete(true);
    }
  };

  const handleSendToMailchimp = async () => {
    if (!apiKey) {
      message.error("Enter your Mailchimp API key first");
      return;
    }
    const srv = server || (apiKey.includes("-") ? apiKey.split("-").pop() : "");
    if (!srv) {
      message.error("Could not detect server prefix. Enter it manually.");
      return;
    }

    setSending(true);
    setResult(null);

    try {
      const resp = await fetch(`${INTEGRATIONS_SERVER}/integrations/test-contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          provider: "mailchimp",
          api_key: apiKey,
          server: srv,
          list_id: "",
          test_email: answers.email,
          test_name: answers.name || answers.email.split("@")[0],
        }),
      });
      const data = await resp.json();
      setResult(data);
      if (data.code === 200) {
        message.success("Contact pushed to Mailchimp!");
      } else {
        message.error(data.message || "Failed");
      }
    } catch (err) {
      setResult({ code: 500, message: err.message });
      message.error("Connection failed — is integrations server running?");
    }
    setSending(false);
  };

  const resetFlow = () => {
    setCurrentStep(0);
    setAnswers({});
    setCurrentAnswer("");
    setFlowComplete(false);
    setResult(null);
  };

  return (
    <div style={{ maxWidth: 700, margin: "40px auto", padding: "0 20px" }}>
      <Title level={3}>🧪 Test: Q&A Flow → Mailchimp</Title>
      <Paragraph type="secondary">
        Simulate an Instagram DM conversation. Answer the questions below, then push all collected data to Mailchimp.
      </Paragraph>

      {/* Mailchimp Config */}
      <Card size="small" style={{ marginBottom: 20, background: "#fafafa" }}>
        <Text strong>Mailchimp Credentials</Text>
        <div style={{ display: "flex", gap: 10, marginTop: 8 }}>
          <Input.Password
            placeholder="Mailchimp API Key (e.g. abc123-us1)"
            value={apiKey}
            onChange={(e) => {
              setApiKey(e.target.value);
              if (e.target.value.includes("-")) setServer(e.target.value.split("-").pop());
            }}
            style={{ flex: 2 }}
          />
          <Input
            placeholder="Server (auto)"
            value={server}
            onChange={(e) => setServer(e.target.value)}
            style={{ flex: 1 }}
          />
        </div>
      </Card>

      <Divider />

      {/* Steps indicator */}
      <Steps
        current={flowComplete ? QUESTIONS.length : currentStep}
        size="small"
        style={{ marginBottom: 24 }}
        items={QUESTIONS.map((q, i) => ({
          title: q.key.charAt(0).toUpperCase() + q.key.slice(1),
          icon: answers[q.key] ? <CheckCircleFilled style={{ color: "#52c41a" }} /> : q.icon,
        }))}
      />

      {/* Q&A Conversation */}
      <Card style={{ minHeight: 300 }}>
        {/* Previous answers */}
        {QUESTIONS.slice(0, flowComplete ? QUESTIONS.length : currentStep).map((q, i) => (
          <div key={q.key} style={{ marginBottom: 16 }}>
            <div style={{ background: "#f0f5ff", padding: "8px 12px", borderRadius: 12, borderBottomLeftRadius: 4, display: "inline-block", maxWidth: "80%" }}>
              <Text>{q.question}</Text>
            </div>
            <div style={{ textAlign: "right", marginTop: 6 }}>
              <div style={{ background: "#1677ff", color: "#fff", padding: "8px 12px", borderRadius: 12, borderBottomRightRadius: 4, display: "inline-block", maxWidth: "80%" }}>
                {answers[q.key]}
              </div>
            </div>
          </div>
        ))}

        {/* Current question */}
        {!flowComplete && (
          <div>
            <div style={{ background: "#f0f5ff", padding: "8px 12px", borderRadius: 12, borderBottomLeftRadius: 4, display: "inline-block", maxWidth: "80%", marginBottom: 12 }}>
              <Text>{QUESTIONS[currentStep].question}</Text>
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <Input
                placeholder={QUESTIONS[currentStep].placeholder}
                value={currentAnswer}
                onChange={(e) => setCurrentAnswer(e.target.value)}
                onPressEnter={handleAnswer}
                autoFocus
              />
              <Button type="primary" icon={<SendOutlined />} onClick={handleAnswer} disabled={!currentAnswer.trim()}>
                Send
              </Button>
            </div>
          </div>
        )}

        {/* Flow complete */}
        {flowComplete && !result && (
          <div style={{ textAlign: "center", padding: "20px 0" }}>
            <div style={{ background: "#f0f5ff", padding: "8px 12px", borderRadius: 12, display: "inline-block", marginBottom: 16 }}>
              <Text>🎉 Thanks! You're all set. Here's your free guide!</Text>
            </div>

            <Divider />

            <Title level={5}>Collected Data</Title>
            <div style={{ textAlign: "left", background: "#f9f9f9", padding: 16, borderRadius: 8, marginBottom: 16 }}>
              <pre style={{ margin: 0, fontSize: 13 }}>{JSON.stringify(answers, null, 2)}</pre>
            </div>

            <Space>
              <Button type="primary" size="large" icon={<RocketOutlined />} loading={sending} onClick={handleSendToMailchimp}>
                Push to Mailchimp
              </Button>
              <Button onClick={resetFlow}>Reset Flow</Button>
            </Space>
          </div>
        )}

        {/* Result */}
        {result && (
          <div style={{ marginTop: 16 }}>
            <Alert
              type={result.code === 200 ? "success" : "error"}
              message={result.code === 200 ? "✅ Pushed to Mailchimp!" : "❌ Failed"}
              description={
                <div>
                  <p>{result.message}</p>
                  {result.code === 200 && (
                    <div style={{ marginTop: 8 }}>
                      <Tag color="blue">Name: {answers.name}</Tag>
                      <Tag color="green">Email: {answers.email}</Tag>
                      <Tag color="orange">Phone: {answers.phone}</Tag>
                      <Tag color="purple">Tag: instantdm</Tag>
                    </div>
                  )}
                  <p style={{ marginTop: 12, fontSize: 12, color: "#888" }}>
                    Check Mailchimp → Audience → All contacts to verify.
                  </p>
                </div>
              }
              showIcon
            />
            <Button onClick={resetFlow} style={{ marginTop: 12 }}>Test Again</Button>
          </div>
        )}
      </Card>
    </div>
  );
}
